import { PageIndices, TabIndices } from './i-doc.registry';
import { NODE_PARENTS, NODE_LABELS, ROUTE_PAGE_OVERRIDES, EXCLUDED_AUTO_TABS } from './i-doc.hierarchy';
import { routeConfig } from '../../routes';

export type IDocType = 'page' | 'tab' | 'modal' | 'section';

const PREFIX_TO_TYPE: Record<string, IDocType> = {
  page: 'page',
  tab: 'tab',
  sec: 'section',
  modal: 'modal',
};

const typeFromId = (id: string): IDocType => PREFIX_TO_TYPE[id.split(':')[0]] ?? 'page';

export interface IDocNode {
  id: string;
  type: IDocType;
  label: string;
  route?: string;
  filePath?: string;
  children?: IDocNode[];
}

export interface IDocDocumentation {
  groups: {
    name: string;
    nodes: IDocNode[];
  }[];
}

/**
 * Розширена документація для вузла i-doc дерева (page/tab/sec/modal) — повний
 * markdown-файл, набагато довший за короткий hint. Файл зберігається як
 * `common/i-doc/docs/<id з ":" замінено на "-">.doc.md`. Ім'я файлу однозначно
 * виводиться з id в обидва боки — без евристик/пошуку підрядків, на відміну
 * від попереднього підходу (файл поруч із компонентом, зіставлення по
 * фрагменту назви файлу), який не мав способу адресувати конкретний tab/modal,
 * якщо той не був "головним" вмістом свого файлу.
 */
const extendedDocs = import.meta.glob('./docs/*.doc.md', { query: '?raw', import: 'default' }) as Record<string, () => Promise<string>>;

const extendedDocKey = (id: string) => `./docs/${id.replace(/:/g, '-')}.doc.md`;

export const hasExtendedDoc = (id: string): boolean => extendedDocKey(id) in extendedDocs;

export const loadExtendedDoc = (id: string): Promise<string> | null => {
  const loader = extendedDocs[extendedDocKey(id)];
  return loader ? loader() : null;
};

export const getDocumentationTree = (role: string = 'expert'): IDocDocumentation => {
  // 1. Filter routes by role — only routes that actually appear in a sidebar
  // (skip detail/param routes like `/client/:id/*` or `/chat/:id`, and
  // sidebar-hidden ones like `/profile` — they aren't independently navigable
  // "pages" for this tree).
  const allowedRoutes = routeConfig.filter(route => {
    if (!route.showInSidebar) return false;
    if (!route.allowedRoles || route.allowedRoles.length === 0) return true;
    // The "admin" preview role stands for both the `admin` and `superadmin`
    // real roles (see userRoles above), so it also picks up superadmin-only
    // routes. BUG FIX: this condition used to run unconditionally (no `role
    // === 'admin'` guard), so it matched for every role — e.g. an
    // superadmin-only route leaked into the "expert" tree too.
    return route.allowedRoles.includes(role) || (role === 'admin' && route.allowedRoles.includes('superadmin'));
  });

  // 2. Group routes
  const groups: Record<string, IDocNode[]> = {};
  const nodeById: Record<string, IDocNode> = {};

  allowedRoutes.forEach(route => {
    const groupName = route.group || 'sidebar.group.general';
    if (!groups[groupName]) groups[groupName] = [];

    // Find PageIndex for this route (explicit override first — see ROUTE_PAGE_OVERRIDES)
    const pageId = ROUTE_PAGE_OVERRIDES[route.path] || Object.values(PageIndices).find(val => {
        const slug = val.split(':')[1];
        // Special case for dashboard-v2 vs dashboard
        if (slug === 'dashboard-v2' && route.path === '/dashboard-v2') return true;
        if (slug === 'dashboard' && route.path === '/dashboard') return true;
        return route.path === `/${slug}`;
    });

    const node: IDocNode = {
      id: pageId || `route:${route.path}`,
      type: 'page',
      label: route.name || route.path,
      route: route.path,
      children: []
    };

    // 3. Find related Tabs (auto, by full page slug prefix — compound ids included)
    if (pageId) {
        const pageSlug = pageId.slice('page:'.length).replace(/:/g, '-');
        const tabPrefix = `tab:${pageSlug}-`;
        const excluded = EXCLUDED_AUTO_TABS[pageId];
        const relatedTabs = Object.values(TabIndices).filter(tabId =>
            tabId.startsWith(tabPrefix) && !excluded?.has(tabId)
        );

        relatedTabs.forEach(tabId => {
            const tabNode: IDocNode = {
                id: tabId,
                type: 'tab',
                label: NODE_LABELS[tabId] || tabId.slice(tabPrefix.length),
            };
            node.children?.push(tabNode);
            nodeById[tabId] = tabNode;
        });
    }

    groups[groupName].push(node);
    nodeById[node.id] = node;
  });

  // 4. Attach nodes that routeConfig can't produce on its own: modals, sections,
  // and pages reached outside the router (e.g. a client's card under "Клієнти").
  // See i-doc.hierarchy.ts. A node may have several parents on purpose — it is
  // then duplicated under each, matching how it's actually reused in the UI.
  // Multiple passes because a child's parent may itself only exist after an
  // earlier pass attached it (e.g. a modal inside a nested page).
  const attached = new Set<string>(); // `${parentId}|${childId}`
  for (let pass = 0; pass < 6; pass++) {
    let addedAny = false;
    Object.entries(NODE_PARENTS).forEach(([childId, parentIds]) => {
      parentIds.forEach(parentId => {
        const key = `${parentId}|${childId}`;
        const parentNode = nodeById[parentId];
        if (!parentNode || attached.has(key)) return;

        const childNode: IDocNode = {
          id: childId,
          type: typeFromId(childId),
          label: NODE_LABELS[childId] || childId,
        };
        (parentNode.children ??= []).push(childNode);
        // Index by id only once, so further children resolve off the first occurrence.
        if (!nodeById[childId]) nodeById[childId] = childNode;
        attached.add(key);
        addedAny = true;
      });
    });
    if (!addedAny) break;
  }

  return {
    groups: Object.entries(groups).map(([name, nodes]) => ({ name, nodes }))
  };
};
